import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { usePortfolioTransactions } from '@/hooks/usePortfolioTransactions';
import { ArrowLeftRight } from 'lucide-react';

interface AddTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TX_TYPES = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
  { value: 'dividend', label: 'Dividend' },
];

export const AddTransactionModal: React.FC<AddTransactionModalProps> = ({ open, onOpenChange }) => {
  const { addTransaction } = usePortfolioTransactions();
  const [symbol, setSymbol] = useState('');
  const [txType, setTxType] = useState<'buy' | 'sell' | 'dividend'>('buy');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [suggestions, setSuggestions] = useState<{ symbol: string; display_name: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (symbol.length < 1) { setSuggestions([]); return; }
    const timeout = setTimeout(async () => {
      const upper = symbol.toUpperCase();
      const { data } = await supabase
        .from('symbol_registry')
        .select('symbol, display_name')
        .ilike('symbol', `%${upper}%`)
        .eq('is_active', true)
        .limit(5);
      setSuggestions(data || []);
      setShowSuggestions((data || []).length > 0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [symbol]);

  const handleSubmit = () => {
    const qty = parseFloat(quantity);
    const px = parseFloat(price);
    if (!symbol || isNaN(qty) || qty <= 0 || isNaN(px) || px <= 0) return;

    addTransaction.mutate({
      symbol: symbol.toUpperCase(),
      transaction_type: txType,
      quantity: qty,
      price: px,
      executed_at: new Date(date).toISOString(),
      notes: notes || undefined,
    });

    setSymbol('');
    setQuantity('');
    setPrice('');
    setNotes('');
    setDate(new Date().toISOString().split('T')[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ArrowLeftRight className="h-5 w-5 text-primary" />
            Add Transaction
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative">
            <Label htmlFor="tx-symbol" className="text-muted-foreground">Symbol</Label>
            <Input
              id="tx-symbol"
              placeholder="e.g. AAPL"
              value={symbol}
              onChange={e => { setSymbol(e.target.value); setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="bg-background"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto">
                {suggestions.map(s => (
                  <button
                    key={s.symbol}
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm text-foreground"
                    onMouseDown={() => { setSymbol(s.symbol); setShowSuggestions(false); }}
                  >
                    <span className="font-medium">{s.symbol}</span>
                    <span className="ml-2 text-muted-foreground">{s.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-muted-foreground">Type</Label>
            <Select value={txType} onValueChange={(v) => setTxType(v as 'buy' | 'sell' | 'dividend')}>
              <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TX_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tx-qty" className="text-muted-foreground">Quantity</Label>
              <Input id="tx-qty" type="number" min="0" step="any" placeholder="0" value={quantity} onChange={e => setQuantity(e.target.value)} className="bg-background" />
            </div>
            <div>
              <Label htmlFor="tx-price" className="text-muted-foreground">Price</Label>
              <Input id="tx-price" type="number" min="0" step="any" placeholder="0.00" value={price} onChange={e => setPrice(e.target.value)} className="bg-background" />
            </div>
          </div>

          <div>
            <Label htmlFor="tx-date" className="text-muted-foreground">Date</Label>
            <Input id="tx-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-background" />
          </div>

          <div>
            <Label htmlFor="tx-notes" className="text-muted-foreground">Notes (optional)</Label>
            <Textarea id="tx-notes" placeholder="Any notes..." value={notes} onChange={e => setNotes(e.target.value)} className="bg-background resize-none" rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={addTransaction.isPending || !symbol || !quantity || !price}>
            {addTransaction.isPending ? 'Recording...' : 'Record Transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
